/**
 * Client Portal module contract.
 *
 * A module declares everything the shared shell + shared list/detail
 * components need to render it: where its data comes from, which fields are
 * client-safe to display, how cards and detail sections are laid out, which
 * filters and actions are allowed, and what access it requires.
 *
 * The Worker remains the security boundary. `clientSafeFields` here is a
 * display-side mirror/allowlist — it does not grant access, it only documents
 * and double-guards which fields the UI is willing to render.
 */

export type ClientFieldFormat =
  | 'text'
  | 'date'
  | 'datetime'
  | 'minutes'
  | 'boolean'
  | 'url'
  | 'currency'
  | 'currency-cents';

export interface ClientFieldDef {
  /** Source field name exactly as returned by the Worker (already safe). */
  source: string;
  label: string;
  format?: ClientFieldFormat;
}

export interface ClientCardConfig {
  /** Field used as the card title; falls back to the record id. */
  title: string;
  subtitle?: string;
  /** Attachment/url field rendered as an avatar/thumbnail. */
  image?: string;
  /** Source fields rendered as short status chips, top-right. */
  badges?: string[];
  /** Key/value facts in the card body. */
  facts?: ClientFieldDef[];
  /** Long-text field rendered as a one-line/clamped blurb. */
  summary?: string;
}

export interface ClientDetailFieldDef extends ClientFieldDef {
  /** Render full-width as a text block instead of an inline fact. */
  long?: boolean;
}

export interface ClientDetailSectionConfig {
  id: string;
  title: string;
  fields: ClientDetailFieldDef[];
}

export interface ClientDetailLink {
  label: string;
  /** Source field holding a safe absolute URL. */
  source: string;
}

export interface ClientDetailConfig {
  title: string;
  subtitle?: string;
  image?: string;
  badges?: string[];
  sections: ClientDetailSectionConfig[];
  /** Safe external links (e.g. a recording URL). Never internal dashboards. */
  links?: ClientDetailLink[];
}

export interface ClientFilterConfig {
  /** Query-param key sent to the Worker (e.g. "status"). */
  id: string;
  label: string;
  /** First option is treated as the "All / no filter" sentinel. */
  options: string[];
}

export type ClientModuleActionId =
  | 'refresh'
  | 'reply'
  | 'qualify'
  | 'archive'
  | 'recycle'
  | 'push-crm'
  | 'callback'
  | 'explain';

export interface ClientModuleAction {
  id: ClientModuleActionId;
  label: string;
  /** Placeholder actions render disabled until a future pass wires them. */
  status?: 'active' | 'coming-soon';
}

export interface ClientModuleSource {
  /** Worker list endpoint path, relative to the API base. */
  endpoint: string;
  /** Expected response envelope `tableKey`, used for response validation. */
  tableKey: string;
  /** Optional explicit detail endpoint; defaults to `${endpoint}/${id}`. */
  detailEndpoint?: string;
}

export type ClientModuleView =
  | 'list'
  | 'homebase'
  | 'organizations'
  | 'switchboard'
  | 'websites'
  | 'work'
  | 'invoices'
  | 'subscriptions'
  | 'alice-activity'
  | 'forms'
  | 'docs';
export type ClientModuleStatus = 'active' | 'coming-soon';

export interface ClientModuleConfig {
  id: string;
  /** App-specific registry key for the records this module renders. */
  recordTypeKey?: string;
  label: string;
  route: string;
  /**
   * Optional sidebar destination when it differs from `route`. Used when a page
   * has multiple tabs and the nav item should open a specific one by default
   * (e.g. Forms opens the Form Entries tab).
   */
  navRoute?: string;
  navGroup: string;
  /** lucide icon key (see platform/icons). */
  icon: string;
  description: string;
  /** 'active' modules render their data; 'coming-soon' render the roadmap state. */
  status?: ClientModuleStatus;
  /** Which shared view renders this module. */
  view?: ClientModuleView;
  /** App id the actor must hold; defaults to the client portal app id. */
  requiredAppId?: string;
  /** Live data source. Optional for coming-soon / bespoke views. */
  source?: ClientModuleSource;
  search?: { placeholder: string; param?: string };
  filters?: ClientFilterConfig[];
  card?: ClientCardConfig;
  detail?: ClientDetailConfig;
  /** Display-side client-safe field allowlist (mirrors the Worker). */
  clientSafeFields?: string[];
  emptyState: { title: string; body: string };
  comingSoon?: { title: string; body: string };
  actions?: ClientModuleAction[];
}

export function defineClientModule(config: ClientModuleConfig): ClientModuleConfig {
  return { status: 'active', view: 'list', ...config };
}
